import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import DashboardLayout from '../common/DashboardLayout';
import StateAdminServices from '../../services/StateAdminServices';
import './StatePages.css';

const STATE_NAMES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

const Analytics = () => {
  const { userrole } = useContext(AuthContext);
  const { type = '' } = useParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const stateId = localStorage.getItem('stateid') || user.stateid || 'IN';
  const stateName = STATE_NAMES[stateId] || stateId;

  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const response = await StateAdminServices.getStateAnalytics();
      if (response.success && response.data) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const response = await StateAdminServices.exportStateData('csv');
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${stateId.toLowerCase()}-analytics-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout role={4} layoutType="topnav">
        <div className="state-page">
          <div className="loading-spinner">Loading {stateName} analytics...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={4} layoutType="topnav">
      <div className="state-page">
        <div className="page-header">
          <h1>{stateName} Analytics</h1>
          <button className="btn-export" onClick={exportData}>
            📊 Export Data
          </button>
        </div>

        {type === 'reports' && analytics && (
          <div className="analytics-grid">
            <div className="analytics-card">
              <h3>City Statistics</h3>
              <div className="city-rankings">
                {analytics.cityStats?.slice(0, 10).map((city, index) => (
                  <div key={city.city} className="city-rank-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>{index + 1}. {city.city}</span>
                    <span>{city.complianceRate}% compliance</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="analytics-card">
              <h3>School Type Distribution</h3>
              <div className="type-distribution">
                {analytics.typeStats?.map((type) => (
                  <div key={type.type} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>{type.type}</span>
                    <span>{type.count} schools</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="analytics-card">
              <h3>Summary</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Schools:</span>
                  <strong>{analytics.totalSchools}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Teachers:</span>
                  <strong>{analytics.totalTeachers}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Certified:</span>
                  <strong>{analytics.totalCertified}</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {type === 'compare' && (
          <div className="analytics-card">
            <h3>School Comparisons</h3>
            <p>School comparison feature coming soon. This will allow you to compare multiple schools side-by-side.</p>
          </div>
        )}

        {!type && analytics && (
          <div className="analytics-grid">
            <div className="analytics-card">
              <h3>City Statistics</h3>
              <div className="city-rankings">
                {analytics.cityStats?.slice(0, 10).map((city, index) => (
                  <div key={city.city} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>{index + 1}. {city.city}</span>
                    <span>{city.complianceRate}% compliance</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="analytics-card">
              <h3>School Type Distribution</h3>
              <div className="type-distribution">
                {analytics.typeStats?.map((type) => (
                  <div key={type.type} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>{type.type}</span>
                    <span>{type.count} schools</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Analytics;

